import get from 'lodash.get';

// Global Utils
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';

// Global Types
import { AppState } from 'services/types';
import { WorkTypeId } from 'services/workTypes/types';
import { selectEntityTypes } from 'services/entityTypes/selectors';

// Local Types
import {
    SchemaId,
    SchemaField,
    SchemasState,
    SchemasProps,
    SchemaEntityTypeId
} from './types';
import { SCHEMA_CATEGORY_CHECKLIST } from './constants';

const selectSchemas = (state: AppState): SchemasState => state.schemas;
const selectSchemaId = (_state: AppState, props: SchemasProps): SchemaId =>
    props.schemaId;

const selectSchemasArray = createDeepEqualSelector(
    selectSchemas,
    (schemasState) => Object.values(schemasState)
);

const selectSchemasExist = createDeepEqualSelector(
    selectSchemasArray,
    (schemasArrayState): boolean => !!schemasArrayState.length
);

const selectSchema = createDeepEqualSelector(
    selectSchemas,
    selectSchemaId,
    (schemasState, schemaId) => get(schemasState, schemaId)
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectSchemaValue = (field: SchemaField) =>
    createDeepEqualSelector(selectSchema, (schemaState) =>
        get(schemaState, field)
    );

const selectSchemasWithoutEventTypeId = createDeepEqualSelector(
    selectSchemasArray,
    (schemasArrayState) =>
        schemasArrayState.filter((value) => !value.eventTypeId)
);

const selectSchemasWithoutEventTypeIdExist = createDeepEqualSelector(
    selectSchemasWithoutEventTypeId,
    (schemasWithoutEventTypeIdState) => !!schemasWithoutEventTypeIdState.length
);

const selectSchemasWithEventTypeId = createDeepEqualSelector(
    selectSchemasArray,
    (schemasArrayState) =>
        schemasArrayState.filter((value) => value.eventTypeId)
);

const selectSchemasWithEventTypeIdExist = createDeepEqualSelector(
    selectSchemasWithEventTypeId,
    (schemasWithEventTypeIdState) => !!schemasWithEventTypeIdState.length
);

const selectHarvestSchema = createDeepEqualSelector(
    selectSchemasArray,
    (schemasArrayState) =>
        schemasArrayState.find(
            (value) => value.eventTypeId === 'HARVEST' && value.isEnabled
        )
);

const selectHarvestSchemaId = createDeepEqualSelector(
    selectHarvestSchema,
    (harvestSchemaState): number | undefined => get(harvestSchemaState, 'id')
);

const selectHarvestSchemaWorkTypeIds = createDeepEqualSelector(
    selectHarvestSchema,
    (harvestSchemaState): WorkTypeId[] | undefined => {
        const harvestSchemaWorkTypes = get(harvestSchemaState, 'workTypes');
        if (harvestSchemaWorkTypes && harvestSchemaWorkTypes.length) {
            return harvestSchemaWorkTypes.map(({ id }) => id);
        }
        return undefined;
    }
);

const selectHarvestSchemaExists = createDeepEqualSelector(
    [selectHarvestSchema],
    (harvestSchemaState): boolean => !!harvestSchemaState
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestSchemaValue = (field: SchemaField) =>
    createDeepEqualSelector([selectHarvestSchema], (harvestSchemaState) =>
        get(harvestSchemaState, field)
    );

const selectHarvestSchemaEntityTypeId = createDeepEqualSelector(
    [selectHarvestSchema],
    (harvestSchemaState): SchemaEntityTypeId =>
        get(harvestSchemaState, 'entityTypeId')
);

const makeSelectHarvestSchemaEntityTypeId =
    (): typeof selectHarvestSchemaEntityTypeId =>
        selectHarvestSchemaEntityTypeId;

const selectSpraySchema = createDeepEqualSelector(
    selectSchemasArray,
    (schemasArrayState) =>
        schemasArrayState.find(
            (value) => value.eventTypeId === 'SPRAY' && value.isEnabled
        )
);

const selectFertilizerSchema = createDeepEqualSelector(
    selectSchemasArray,
    (schemasArrayState) =>
        schemasArrayState.find(
            (value) => value.eventTypeId === 'FERTILIZER' && value.isEnabled
        )
);

const selectFertilizerSchemaId = createDeepEqualSelector(
    selectFertilizerSchema,
    (fertilizerSchemaState): number | undefined =>
        get(fertilizerSchemaState, 'id')
);

const selectFertilizerEntityTypeId = createDeepEqualSelector(
    selectFertilizerSchema,
    (fertilizerSchemaState): number | undefined =>
        get(fertilizerSchemaState, 'entityTypeId')
);

const selectFertilizerSchemaEntityTypeName = createDeepEqualSelector(
    [selectFertilizerEntityTypeId, selectEntityTypes],
    (fertilizerSchemaEntityTypeId, entityTypesState): string | undefined => {
        if (!fertilizerSchemaEntityTypeId) {
            return undefined;
        }
        const spraySchemaEntityType = get(
            entityTypesState,
            fertilizerSchemaEntityTypeId
        );
        return get(spraySchemaEntityType, 'name');
    }
);

const selectFertilizerSchemaInnerEntityTypeId = createDeepEqualSelector(
    selectFertilizerSchema,
    (fertilizerSchemaState): number | undefined =>
        get(fertilizerSchemaState, 'innerEntityTypeId')
);

const selectFertilizerSchemaInnerEntityTypePluralName = createDeepEqualSelector(
    [selectFertilizerSchemaInnerEntityTypeId, selectEntityTypes],
    (
        fertilizerSchemaInnerEntityTypeId,
        entityTypesState
    ): string | null | undefined => {
        if (!fertilizerSchemaInnerEntityTypeId) {
            return undefined;
        }
        const spraySchemaEntityType = get(
            entityTypesState,
            fertilizerSchemaInnerEntityTypeId
        );
        return get(spraySchemaEntityType, 'pluralName', '');
    }
);

const selectSpraySchemaExists = createDeepEqualSelector(
    selectSpraySchema,
    (spraySchemaState): boolean => !!spraySchemaState
);

const selectSpraySchemaId = createDeepEqualSelector(
    selectSpraySchema,
    (spraySchemaState): number | undefined => get(spraySchemaState, 'id')
);

const selectSpraySchemaEntityTypeId = createDeepEqualSelector(
    selectSpraySchema,
    (spraySchemaState): number | undefined =>
        get(spraySchemaState, 'entityTypeId')
);

const selectSpraySchemaEntityTypeName = createDeepEqualSelector(
    [selectSpraySchemaEntityTypeId, selectEntityTypes],
    (spraySchemaEntityTypeId, entityTypesState): string | undefined => {
        if (!spraySchemaEntityTypeId) {
            return undefined;
        }
        const spraySchemaEntityType = get(
            entityTypesState,
            spraySchemaEntityTypeId
        );
        return get(spraySchemaEntityType, 'name');
    }
);

const selectSpraySchemaInnerEntityTypeId = createDeepEqualSelector(
    selectSpraySchema,
    (spraySchemaState): number | undefined =>
        get(spraySchemaState, 'innerEntityTypeId')
);

const selectSpraySchemaInnerEntityTypePluralName = createDeepEqualSelector(
    [selectSpraySchemaInnerEntityTypeId, selectEntityTypes],
    (
        spraySchemaInnerEntityTypeId,
        entityTypesState
    ): string | null | undefined => {
        if (!spraySchemaInnerEntityTypeId) {
            return undefined;
        }
        const spraySchemaEntityType = get(
            entityTypesState,
            spraySchemaInnerEntityTypeId
        );
        return get(spraySchemaEntityType, 'pluralName', '');
    }
);

const selectEnabledSchemas = createDeepEqualSelector(
    selectSchemasArray,
    (schemas) =>
        schemas.filter((schema) => !schema.eventTypeId && schema.isEnabled)
);

const selectEnabledGeneralSchemas = createDeepEqualSelector(
    selectEnabledSchemas,
    (schemas) =>
        schemas.filter(
            (schema) => schema.category !== SCHEMA_CATEGORY_CHECKLIST
        )
);

const selectEnabledChecklistSchemas = createDeepEqualSelector(
    selectEnabledSchemas,
    (schemas) =>
        schemas.filter(
            (schema) => schema.category === SCHEMA_CATEGORY_CHECKLIST
        )
);

const selectFertilizerSchemaExists = createDeepEqualSelector(
    selectFertilizerSchema,
    (fertilizerSchemaState) => !!fertilizerSchemaState
);

export {
    selectSchemas,
    selectSchemaId,
    selectSchemasArray,
    selectSchemasExist,
    selectSchema,
    makeSelectSchemaValue,
    selectSchemasWithoutEventTypeId,
    selectSchemasWithoutEventTypeIdExist,
    selectSchemasWithEventTypeId,
    selectSchemasWithEventTypeIdExist,
    selectHarvestSchema,
    selectHarvestSchemaExists,
    selectHarvestSchemaId,
    selectHarvestSchemaWorkTypeIds,
    makeSelectHarvestSchemaValue,
    selectSpraySchema,
    selectSpraySchemaExists,
    selectSpraySchemaId,
    selectSpraySchemaEntityTypeId,
    selectSpraySchemaInnerEntityTypeId,
    selectSpraySchemaEntityTypeName,
    selectSpraySchemaInnerEntityTypePluralName,
    makeSelectHarvestSchemaEntityTypeId,
    selectEnabledSchemas,
    selectEnabledGeneralSchemas,
    selectEnabledChecklistSchemas,
    selectFertilizerSchema,
    selectFertilizerSchemaExists,
    selectFertilizerSchemaEntityTypeName,
    selectFertilizerSchemaInnerEntityTypePluralName,
    selectFertilizerEntityTypeId,
    selectFertilizerSchemaInnerEntityTypeId,
    selectFertilizerSchemaId
};
